import type { ApiResponse, FetchHeaders } from '@/interfaces';
import getConfig from 'next/config';

const getBasePath = () => getConfig().publicRuntimeConfig.basePath;
const getLocalUrl = () => getConfig().publicRuntimeConfig.localUrl;

const options = (headers: FetchHeaders, signal?: AbortSignal): RequestInit => ({
    method: 'GET',
    headers: {
        Accept: 'application/json',
        ...headers,
    },
    signal,
});

const parseJson = async <T>(res: Response, url): Promise<ApiResponse<T>> => {
    if (res.status >= 200 && res.status < 300) {
        return res.json().then((data) => ({ status: res.status, data, url }));
    }
    return Promise.resolve({ status: res.status, url });
};

const getApiUrl = (url: string): string => `${process.browser ? '' : getLocalUrl()}${getBasePath()}${url}`;

export const fetchFromApiPlain = (url: string, header: FetchHeaders = {}, signal?: AbortSignal): Promise<ApiResponse<string>> => {
    const apiUrl: string = getApiUrl(url);
    return fetch(apiUrl, options(header, signal))
        .then((res) =>
            res.text().then((text) => ({
                status: res.status,
                url: apiUrl,
                data: text,
            })),
        )
        .catch((error) => ({ error, url: apiUrl }));
};

export const fetchFromApi = <T>(url: string, headers: FetchHeaders = {}, signal?: AbortSignal): Promise<ApiResponse<T>> => {
    const apiUrl: string = getApiUrl(url);
    return fetch(apiUrl, options(headers, signal))
        .then((res) => parseJson<T>(res, url))
        .catch((error) => ({ error, url }));
};

export const postToApi = <T, B extends BodyInit>(
    url: string,
    body: B,
    headers: FetchHeaders = {},
    signal?: AbortSignal,
): Promise<ApiResponse<T>> => {
    const apiUrl: string = getApiUrl(url);
    return fetch(apiUrl, { ...options(headers, signal), method: 'POST', body })
        .then((res) => parseJson<T>(res, url))
        .catch((error) => ({ error, url }));
};
